import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, RefreshCw, RotateCw, UserRoundCog } from "lucide-react";
import type { ProductionPinStatus } from "./models";
import { productionGateway } from "./production-gateway";

const roleLabel = (role: ProductionPinStatus["role"]) =>
  role === "VARDIYA_AMIRI" ? "Vardiya amiri" : "Üretim operatörü";

const pinUpdatedAt = (value: string | null) =>
  value ? new Date(value).toLocaleString("tr-TR") : "Henüz üretilmedi";

export function SupervisorPinCard({ onSaved, notify }: { onSaved: () => Promise<void>; notify: (message: string) => void }) {
  const [items, setItems] = useState<ProductionPinStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyPersonId, setBusyPersonId] = useState<string | null>(null);
  const [confirmPersonId, setConfirmPersonId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ personId: string; pin: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const result = await productionGateway.productionPins();
      setItems(result.items);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "PIN kasası yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!revealed) return;
    const timeout = window.setTimeout(() => setRevealed(null), 30_000);
    return () => window.clearTimeout(timeout);
  }, [revealed]);

  const generate = async (person: ProductionPinStatus) => {
    if (busyPersonId) return;
    if (person.hasPin && confirmPersonId !== person.personId) {
      setConfirmPersonId(person.personId);
      setRevealed(null);
      return;
    }
    setBusyPersonId(person.personId);
    setConfirmPersonId(null);
    try {
      const result = await productionGateway.generateProductionPin(person.personId, person.updatedAt);
      setRevealed({ personId: person.personId, pin: result.pin });
      notify(`${person.personName} için sistem yeni PIN üretti.`);
      await Promise.all([load(), onSaved()]);
    } catch (error) {
      notify(error instanceof Error ? error.message : "PIN üretilemedi.");
    } finally {
      setBusyPersonId(null);
    }
  };

  const reveal = async (person: ProductionPinStatus) => {
    if (busyPersonId) return;
    if (revealed?.personId === person.personId) {
      setRevealed(null);
      return;
    }
    setBusyPersonId(person.personId);
    setConfirmPersonId(null);
    try {
      const result = await productionGateway.revealProductionPin(person.personId);
      setRevealed({ personId: person.personId, pin: result.pin });
    } catch (error) {
      notify(error instanceof Error ? error.message : "PIN görüntülenemedi.");
    } finally {
      setBusyPersonId(null);
    }
  };

  return <section className="machine-terminal-card supervisor-pin-card personnel-pin-vault" aria-labelledby="personnel-pin-vault-title">
    <header className="supervisor-pin-header">
      <span><KeyRound size={22} aria-hidden="true" /></span>
      <div><h3 id="personnel-pin-vault-title">Personel PIN kasası</h3><p>PIN’i sistem üretir. Yalnız sistem yöneticisi görüntüler veya yeniler.</p></div>
      <button type="button" disabled={loading || Boolean(busyPersonId)} onClick={() => void load()}><RotateCw size={17} aria-hidden="true" />{loading ? "Yükleniyor…" : "Listeyi yenile"}</button>
    </header>
    <p className="supervisor-pin-role-note">Vardiya amiri ve üretim operatörü rolleri Kullanıcılar ekranında yalnız sistem yöneticisi tarafından atanır. Eski hash-only PIN’ler görüntülenemez; bir kez yeniden üretilmeleri gerekir.</p>
    {loading ? <div className="supervisor-pin-loading" role="status">Vardiya amirleri ve operatörler yükleniyor…</div>
      : loadError ? <div className="supervisor-pin-empty" role="alert"><UserRoundCog size={24} aria-hidden="true" /><div><strong>PIN kasası yüklenemedi</strong><small>{loadError}</small></div><button type="button" onClick={() => void load()}>Yeniden dene</button></div>
      : !items.length ? <div className="supervisor-pin-empty"><UserRoundCog size={24} aria-hidden="true" /><div><strong>PIN atanabilecek çalışan yok</strong><small>Kullanıcılar ekranında Vardiya Amiri veya Üretim Operatörü rolü atayın.</small></div></div>
      : <div className="personnel-pin-list" role="list">
        {items.map((person) => {
          const isBusy = busyPersonId === person.personId;
          const vaultBusy = Boolean(busyPersonId);
          const isRevealed = revealed?.personId === person.personId;
          const needsMigration = person.hasPin && !person.revealable;
          return <article key={person.personId} role="listitem" className={isRevealed ? "is-revealed" : ""}>
            <div className="personnel-pin-person"><strong>{person.personName}</strong><span>{roleLabel(person.role)}</span><small>{person.lockedUntil ? `Geçici kilit: ${new Date(person.lockedUntil).toLocaleString("tr-TR")}` : pinUpdatedAt(person.updatedAt)}</small></div>
            <div className="personnel-pin-state"><b>{!person.hasPin ? "PIN yok" : needsMigration ? "Yeniden üretilecek" : "PIN hazır"}</b>{isRevealed ? <output aria-label={`${person.personName} PIN kodu`}>{revealed.pin}</output> : null}</div>
            <div className="personnel-pin-actions">
              <button type="button" disabled={vaultBusy || !person.revealable} onClick={() => void reveal(person)}>{isRevealed ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}{isBusy ? "Açılıyor…" : isRevealed ? "Gizle" : "PIN’i göster"}</button>
              <button type="button" disabled={vaultBusy} onClick={() => void generate(person)}><RefreshCw size={17} aria-hidden="true" />{isBusy ? "Üretiliyor…" : person.hasPin ? "Yeni PIN üret" : "PIN üret"}</button>
            </div>
            {confirmPersonId === person.personId ? <div className="personnel-pin-confirm" role="alert"><span><strong>Eski PIN hemen geçersiz olacak.</strong><small>{person.personName} yeni PIN ile giriş yapacak.</small></span><button type="button" onClick={() => setConfirmPersonId(null)}>Vazgeç</button><button type="button" onClick={() => void generate(person)}>Onayla ve üret</button></div> : null}
          </article>;
        })}
      </div>}
    {revealed ? <p className="personnel-pin-notice" role="status" aria-live="polite">PIN görüntülendi ve denetim kaydına yazıldı. 30 saniye sonra otomatik gizlenir.</p> : null}
  </section>;
}
